import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { readFavourites } from "../lib/favourites_store"

// Fills each day's walk slot with the walk that day's favourites make. Favourites are kept on this
// device, so their ids go to the server, which answers with a Turbo Stream; it reloads when hearts change.
export default class extends Controller {
  static values = { url: String, slug: String, variant: { type: String, default: "pill" }, prompts: { type: Boolean, default: true } }

  connect() {
    this.loadedIds = null
    this.beforeStreamRender = this.beforeStreamRender.bind(this)
    document.addEventListener("turbo:before-stream-render", this.beforeStreamRender)
    this.load()
  }

  disconnect() {
    clearTimeout(this.timer)
    this.abort?.abort()
    document.removeEventListener("turbo:before-stream-render", this.beforeStreamRender)
  }

  // A snapping row holds on to the card it was showing when a walk is slotted in before it. A row
  // still at its start should open on the new walk, so it goes back to the start once the walk is in.
  beforeStreamRender(event) {
    const id = event.target.getAttribute?.("target")
    const row = id && this.element.querySelector(`#${CSS.escape(id)}`)?.parentElement
    if (!row || row.scrollLeft > 4) return

    const render = event.detail.render
    event.detail.render = async (stream) => {
      await render(stream)
      row.scrollLeft = 0
    }
  }

  refresh() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.load(), 300)
  }

  showingWalks() {
    return Array.from(this.element.querySelectorAll("[id^='favourite-walk-']")).some(slot => slot.children.length > 0)
  }

  async load() {
    const ids = readFavourites(this.slugValue).sort((a, b) => a - b)
    const key = ids.join(",")
    if (key === this.loadedIds) return

    // Nothing to fill in yet: the page already shows the invitations to plan your own (unless it's a
    // cached snapshot still showing an old walk of yours)
    const first = this.loadedIds === null
    this.loadedIds = key
    if (first && ids.length < 2 && !this.showingWalks()) return

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("ids", key)
    url.searchParams.set("variant", this.variantValue)
    url.searchParams.set("prompts", this.promptsValue)

    this.abort?.abort()
    this.abort = new AbortController()
    try {
      const response = await fetch(url, { headers: { Accept: "text/vnd.turbo-stream.html" }, signal: this.abort.signal })
      if (response.ok) Turbo.renderStreamMessage(await response.text())
    } catch (error) {
      if (error.name !== "AbortError") console.warn("Couldn't load your walks:", error)
    }
  }
}
